<script>
import ProfileLayout from "@/Layouts/ProfileLayout.vue";

export default {
    layout: ProfileLayout,
};
</script>

<script setup>
import { computed } from "vue";
import { Head, usePage } from "@inertiajs/vue3";
import { LIST_SORT_OPTIONS, useListGrid } from "@/composables/useListGrid.js";
import CreateListButton from "@/Components/Profile/CreateListButton.vue";
import ListsPagination from "@/Components/Profile/ListsPagination.vue";
import ProfileHero from "@/Components/Profile/ProfileHero.vue";
import ProfileTabs from "@/Components/Profile/ProfileTabs.vue";
import PromoBanner from "@/Components/Profile/PromoBanner.vue";
import SortDropdown from "@/Components/Profile/SortDropdown.vue";
import WishListCard from "@/Components/Profile/WishListCard.vue";

// Access props through the page object so in-place reloads (e.g. after the
// Manage Profile modalog saves) aren't read from a stale props reference.
const inertiaPage = usePage();

const owner = computed(() => inertiaPage.props.owner?.data);

// One combined grid: expert lists carry their own badge (per Figma).
const allLists = computed(() => [
    ...(inertiaPage.props.expertlists?.data ?? []),
    ...(inertiaPage.props.giftlists?.data ?? []),
]);

const { sort, page, pages, paged } = useListGrid(allLists);

const ownerShortName = computed(() =>
    owner.value.is_organization
        ? owner.value.presentation_name
        : owner.value.first_name,
);
</script>

<template>
    <div>
        <Head>
            <title>My Gift Hero | Gift Hero</title>
        </Head>

        <ProfileHero
            :owner="owner"
            :is-owner="true"
            :theme="owner.profile_theme"
        />

        <ProfileTabs :owner="owner" :is-owner="true" active="lists">
            <CreateListButton class="w-full max-lg:order-2 lg:hidden" />
        </ProfileTabs>

        <div
            class="px-4 pb-8 max-lg:bg-brand-cream max-lg:pt-6 sm:px-[35px] sm:pb-9"
        >
            <section
                class="rounded-brand bg-brand-cream px-4 py-6 max-lg:p-0 sm:px-[46px] sm:py-8"
            >
                <header
                    class="flex flex-col gap-4 lg:grid lg:grid-cols-[1fr_auto_1fr] lg:items-center"
                >
                    <CreateListButton
                        class="justify-self-start max-lg:hidden"
                    />
                    <h2
                        class="text-center font-brand text-[25px] font-bold leading-none text-brand-navy lg:text-[34px]"
                    >
                        {{ ownerShortName }}’s Lists
                    </h2>
                    <SortDropdown
                        v-model="sort"
                        :options="LIST_SORT_OPTIONS"
                        class="lg:justify-self-end"
                    />
                </header>

                <template v-if="allLists.length">
                    <div
                        class="mt-7 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3"
                    >
                        <WishListCard
                            v-for="list in paged"
                            :key="list.uuid"
                            :list="list"
                            :is-owner="true"
                            :owner-is-gift-hero="!!owner.is_gift_hero"
                        />
                    </div>

                    <ListsPagination
                        v-if="pages > 1"
                        v-model:page="page"
                        :pages="pages"
                        class="mt-10"
                    />
                </template>

                <div
                    v-else
                    class="flex flex-col items-center gap-5 py-14 text-center"
                >
                    <p class="max-w-md font-brand text-[16px] text-[#565a77]">
                        You haven&rsquo;t created any lists yet. Create your
                        first list to start adding gifts.
                    </p>
                    <CreateListButton />
                </div>
            </section>

            <PromoBanner
                class="mt-8"
                headline="Need Some
Giftspiration?"
                body="Browse hand-picked gift ideas from our experts and find something they’ll actually love — for any occasion, any budget."
                cta-label="View Expert Lists"
                :cta-href="route('explore')"
                image="images/profile/cta-collage.png"
                mobile-image="images/profile/cta-collage-mobile.png"
                variant="navy"
            />
        </div>
    </div>
</template>
